/**
 * PDF / Excel export helpers for Reports hub.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const ACCENT_RGB = [76, 225, 182];

/**
 * @param {{ key: string, label: string }[]} columns
 * @param {Record<string, unknown>[]} rows
 */
export function downloadExcel(columns, rows, filename, sheetName = 'Report') {
  const headers = columns.map((c) => c.label);
  const keys = columns.map((c) => c.key);
  const data = rows.map((row) => {
    const o = {};
    keys.forEach((k, i) => {
      const v = row[k];
      o[headers[i]] = v === null || v === undefined ? '' : v;
    });
    return o;
  });
  if (!data.length) {
    data.push(Object.fromEntries(headers.map((h) => [h, ''])));
  }
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  const safeName = String(sheetName || 'Report').slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, safeName);
  const name = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, name);
}

/**
 * @param {{ key: string, label: string }[]} columns
 * @param {Record<string, unknown>[]} rows
 */
export function downloadPdfTable(title, subtitle, columns, rows, filename, options = {}) {
  const { landscape = false, fontSize = 7 } = options;
  const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait', unit: 'mm' });
  doc.setFontSize(15);
  doc.setTextColor(30, 30, 35);
  doc.text(title, 14, 14);
  let startY = 20;
  if (subtitle) {
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 90);
    const lines = doc.splitTextToSize(subtitle, landscape ? 270 : 180);
    doc.text(lines, 14, 20);
    startY = 22 + lines.length * 4;
  }
  const head = [columns.map((c) => c.label)];
  const body = rows.map((row) =>
    columns.map((c) => {
      const v = row[c.key];
      if (v === null || v === undefined) return '';
      return String(v);
    })
  );
  autoTable(doc, {
    startY,
    head,
    body,
    styles: { fontSize, cellPadding: 1.2, overflow: 'linebreak' },
    headStyles: { fillColor: ACCENT_RGB, textColor: 20, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 252, 250] },
    margin: { left: 14, right: 14 },
  });
  const name = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  doc.save(name);
}

/**
 * Multiple sections in one PDF (e.g. payments + expenses).
 * @param {{ title: string, columns: {key:string,label:string}[], rows: Record<string,unknown>[] }[]} sections
 */
export function downloadPdfMultiSection(mainTitle, subtitle, sections, filename, options = {}) {
  const { landscape = true, fontSize = 7 } = options;
  const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait', unit: 'mm' });
  doc.setFontSize(15);
  doc.text(mainTitle, 14, 14);
  let y = 22;
  if (subtitle) {
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 90);
    const lines = doc.splitTextToSize(subtitle, landscape ? 270 : 180);
    doc.text(lines, 14, 20);
    y = 24 + lines.length * 4;
  }
  sections.forEach((sec, idx) => {
    if (idx > 0) y += 6;
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 35);
    doc.text(sec.title, 14, y);
    y += 5;
    const head = [sec.columns.map((c) => c.label)];
    const body = sec.rows.map((row) =>
      sec.columns.map((c) => {
        const v = row[c.key];
        return v === null || v === undefined ? '' : String(v);
      })
    );
    autoTable(doc, {
      startY: y,
      head,
      body,
      styles: { fontSize, cellPadding: 1.2 },
      headStyles: { fillColor: ACCENT_RGB, textColor: 20, fontStyle: 'bold' },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 4;
  });
  const name = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  doc.save(name);
}

/* -------------------------------------------------------------------------- */
/* Premium PDF — Insights → Reports only (jsPDF + autoTable).                 */
/* BI-style executive dashboard: KPIs, charts, refined table, brand footer.   */
/* Stock / POS keep using downloadPdfTable / downloadPdfMultiSection.         */
/* RoomNexa palette: teal #00897B, mint #4CE1B6. Excel export untouched.      */
/* -------------------------------------------------------------------------- */

const PDF_BRAND = {
  titleTeal: [0, 137, 123],
  mint: [76, 225, 182],
  mintDeep: [0, 137, 123],
  cream: [224, 242, 241],
  surface: [245, 248, 250],
  cardBg: [255, 255, 255],
  zebra: [240, 253, 250],
  zebraAlt: [255, 255, 255],
  text: [26, 26, 26],
  muted: [84, 110, 122],
  white: [255, 255, 255],
  border: [0, 137, 123],
  softBorder: [178, 223, 219],
  chartTeal: [0, 137, 123],
  chartMint: [76, 225, 182],
  chartBlue: [41, 182, 246],
  chartPink: [240, 98, 146],
  chartPurple: [126, 87, 194],
  chartOrange: [255, 167, 38],
  chartGreen: [102, 187, 106],
  // Status badges
  badgeCheckedIn: [0, 137, 123],
  badgeCheckedOut: [46, 125, 50],
  badgeConfirmed: [194, 24, 91],
  badgeCancelled: [198, 40, 40],
  badgeNoShow: [239, 108, 0],
  badgePending: [245, 124, 0],
  badgePartial: [255, 160, 0],
  badgeDefault: [96, 125, 139],
  // Soft row tints
  rowCheckedIn: [224, 242, 241],
  rowCheckedOut: [232, 245, 233],
  rowConfirmed: [252, 228, 236],
  rowCancelled: [255, 235, 238],
  rowNoShow: [255, 243, 224],
  rowPartialPay: [255, 248, 225],
  rowComp: [255, 253, 231],
  rowFailed: [255, 235, 238],
  rowDefault: [255, 255, 255],
  // Conditional highlights
  warnMissing: [255, 243, 224],
  warnForeign: [227, 242, 253],
  warnLongStay: [243, 229, 245],
};

const PDF_CHART_PALETTE = [
  PDF_BRAND.chartTeal,
  PDF_BRAND.chartMint,
  PDF_BRAND.chartBlue,
  PDF_BRAND.chartPink,
  PDF_BRAND.chartPurple,
  PDF_BRAND.chartOrange,
  PDF_BRAND.chartGreen,
];

function pdfNorm(v) {
  return String(v ?? '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .trim();
}

function pdfIsBlank(v) {
  if (v == null) return true;
  const s = String(v).trim();
  return !s || s === '—' || s === '-' || s === 'n/a' || s === 'na';
}

function pdfFindCol(cols, patterns) {
  return cols.find((c) => {
    const t = `${c.key || ''} ${c.label || ''}`.toLowerCase();
    return patterns.some((re) => re.test(t));
  });
}

function pdfStatusKind(raw) {
  const s = pdfNorm(raw);
  if (!s) return null;
  if (/\b(comp\.?|complimentary)\b/.test(s)) return 'comp';
  if (/\bcancel/.test(s)) return 'cancelled';
  if (/\bno.?show\b/.test(s)) return 'noshow';
  if (/\b(failed|refund)\b/.test(s)) return 'failed';
  if (/\bpartial/.test(s) || /\bdue\b/.test(s)) return 'partial';
  if (/\bchecked.?out\b/.test(s) || /\bcompleted\b/.test(s)) return 'checked_out';
  if (/\bchecked.?in\b/.test(s)) return 'checked_in';
  if (/\bconfirmed\b/.test(s) || /\bbooked\b/.test(s)) return 'confirmed';
  if (/\bpending\b/.test(s)) return 'pending';
  return null;
}

function pdfStatusBadgeColors(kind) {
  switch (kind) {
    case 'checked_in':
      return { fill: PDF_BRAND.badgeCheckedIn, text: PDF_BRAND.white };
    case 'checked_out':
      return { fill: PDF_BRAND.badgeCheckedOut, text: PDF_BRAND.white };
    case 'confirmed':
      return { fill: PDF_BRAND.badgeConfirmed, text: PDF_BRAND.white };
    case 'cancelled':
    case 'failed':
      return { fill: PDF_BRAND.badgeCancelled, text: PDF_BRAND.white };
    case 'noshow':
      return { fill: PDF_BRAND.badgeNoShow, text: PDF_BRAND.white };
    case 'pending':
      return { fill: PDF_BRAND.badgePending, text: PDF_BRAND.white };
    case 'partial':
      return { fill: PDF_BRAND.badgePartial, text: PDF_BRAND.text };
    case 'comp':
      return { fill: PDF_BRAND.rowComp, text: PDF_BRAND.text };
    default:
      return { fill: PDF_BRAND.badgeDefault, text: PDF_BRAND.white };
  }
}

function pdfRowFillFromData(row) {
  const blob = [
    row?.status,
    row?.payment_status,
    row?.pay_status,
    row?.note,
    row?.notes,
    row?.remark,
    row?.remarks,
    row?.clean_status,
  ]
    .filter((v) => v != null && v !== '')
    .map((v) => pdfNorm(v))
    .join(' | ');
  if (!blob) return null;
  const kind = pdfStatusKind(blob);
  if (kind === 'comp') return PDF_BRAND.rowComp;
  if (kind === 'cancelled' || kind === 'failed') return PDF_BRAND.rowCancelled;
  if (kind === 'noshow') return PDF_BRAND.rowNoShow;
  if (kind === 'partial') return PDF_BRAND.rowPartialPay;
  if (kind === 'checked_out') return PDF_BRAND.rowCheckedOut;
  if (kind === 'checked_in') return PDF_BRAND.rowCheckedIn;
  if (kind === 'confirmed') return PDF_BRAND.rowConfirmed;
  return null;
}

function pdfHeaderFillForColumn(col) {
  const t = `${col?.key || ''} ${col?.label || ''}`.toLowerCase();
  if (/\b(total|amount|price|spent|balance|gst|tax|cur|currency)\b/.test(t)) return [0, 121, 107];
  if (/\b(pay|payment|paid|due|refund)\b/.test(t)) return [46, 125, 50];
  if (/\b(status|remark|note|channel|type|purpose)\b/.test(t)) return [173, 20, 87];
  if (/\b(check.?in|check.?out|arrival|departure|night|room|booked|date)\b/.test(t)) return [94, 53, 177];
  if (/\b(guest|email|phone|name|booking|id|nationality|country|address)\b/.test(t)) return [0, 137, 123];
  return [2, 136, 209];
}

function pdfIconPrefix(col) {
  const t = `${col?.key || ''} ${col?.label || ''}`.toLowerCase();
  if (/\b(guest|name)\b/.test(t) && !/\b(email|phone)\b/.test(t)) return 'G · ';
  if (/\bphone\b/.test(t)) return 'P · ';
  if (/\broom\b/.test(t)) return 'R · ';
  if (/\b(country|nationality)\b/.test(t)) return 'C · ';
  if (/\bbooking\b/.test(t) || /\bcode\b/.test(t)) return 'B · ';
  if (/\bid\b/.test(t) && /\b(number|type|document)\b/.test(t)) return 'ID · ';
  return '';
}

/** Load a remote/same-origin image into a data URL for jsPDF addImage. */
async function loadPdfImageDataUrl(url) {
  const src = String(url || '').trim();
  if (!src) return null;
  const isAbsoluteHttp = /^https?:\/\//i.test(src);
  try {
    // Cross-origin GCS/CDN URLs often fail with credentials: 'include'
    const response = await fetch(src, {
      credentials: isAbsoluteHttp ? 'omit' : 'include',
      mode: 'cors',
    });
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob || !String(blob.type || '').startsWith('image/')) {
      // Some CDNs return octet-stream; still try to decode as image
      if (!blob || blob.size < 32) return null;
    }
    const objectUrl = URL.createObjectURL(blob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      const w = image.naturalWidth || 120;
      const h = image.naturalHeight || 120;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0, w, h);
      const type = String(blob.type || '').toLowerCase();
      const isJpeg = /jpe?g/.test(type) || /\.jpe?g(\?|$)/i.test(src);
      return {
        dataUrl: canvas.toDataURL(isJpeg ? 'image/jpeg' : 'image/png'),
        format: isJpeg ? 'JPEG' : 'PNG',
        aspect: w > 0 ? h / w : 1,
      };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    // Fallback: draw via <img crossOrigin> (still needs CORS on the image host)
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
      const canvas = document.createElement('canvas');
      const w = image.naturalWidth || 120;
      const h = image.naturalHeight || 120;
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(image, 0, 0, w, h);
      const isJpeg = /\.jpe?g(\?|$)/i.test(src);
      return {
        dataUrl: canvas.toDataURL(isJpeg ? 'image/jpeg' : 'image/png'),
        format: isJpeg ? 'JPEG' : 'PNG',
        aspect: w > 0 ? h / w : 1,
      };
    } catch {
      return null;
    }
  }
}

/**
 * Derive executive KPIs + chart series per report type from visible columns.
 * No API / business-logic changes — pure presentation aggregates.
 */
function pdfParseMoney(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/,/g, '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function pdfFormatMoney(n) {
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function pdfCountByCol(rows, col, limit = 6) {
  if (!col) return [];
  const counts = {};
  rows.forEach((row) => {
    const label = pdfIsBlank(row[col.key]) ? 'Unknown' : String(row[col.key]).trim();
    counts[label] = (counts[label] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);
}

function pdfSumCol(rows, col) {
  if (!col) return 0;
  return rows.reduce((sum, row) => sum + (pdfParseMoney(row[col.key]) ?? 0), 0);
}

function pdfDayTrend(rows, col, limit = 10) {
  if (!col) return [];
  const counts = {};
  rows.forEach((row) => {
    if (pdfIsBlank(row[col.key])) return;
    const day = String(row[col.key]).trim().slice(0, 10);
    counts[day] = (counts[day] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-limit);
}

/** Stay statuses only — never treat restaurant "Completed" as checked-out. */
function pdfStayKind(raw) {
  const s = pdfNorm(raw);
  if (!s) return null;
  if (/\bchecked.?in\b/.test(s)) return 'checked_in';
  if (/\bchecked.?out\b/.test(s)) return 'checked_out';
  if (/\bconfirmed\b/.test(s) || /\bbooked\b/.test(s)) return 'confirmed';
  if (/\bcancel/.test(s)) return 'cancelled';
  if (/\bno.?show\b/.test(s)) return 'noshow';
  if (/\bpending\b/.test(s)) return 'pending';
  return null;
}

function detectPdfReportKind(reportType, cols) {
  const rt = String(reportType || '')
    .toLowerCase()
    .trim();
  if (
    [
      'booking',
      'police',
      'restaurant',
      'finance',
      'gst',
      'inventory',
      'customer',
    ].includes(rt)
  ) {
    return rt;
  }
  const keys = cols.map((c) => String(c.key || '').toLowerCase()).join(' ');
  if (/\border_number\b|\border_type\b|\border_date\b/.test(keys)) return 'restaurant';
  if (/\bid_number\b|\bnationality\b|\bno_of_guests\b/.test(keys)) return 'police';
  if (/\broom_revenue\b|\bcgst|\binvoice_total\b/.test(keys)) return 'gst';
  if (/\bclean_status\b|\bis_available\b|\broom_type\b/.test(keys)) return 'inventory';
  if (/\bloyalty|\btotal_spent\b|\blast_booking\b/.test(keys)) return 'customer';
  if (/\bpayment_method\b|\bpayment_date\b|\bamount\b/.test(keys) && !/\bcheck_in/.test(keys)) {
    return 'finance';
  }
  return 'booking';
}

function computePdfAnalytics(cols, rows, reportType = '') {
  const kind = detectPdfReportKind(reportType, cols);
  const statusCol = pdfFindCol(cols, [/^status$/, /\bstatus\b/, /payment_status/]);

  if (kind === 'restaurant') {
    const totalCol = pdfFindCol(cols, [/total_amount/, /^total$/]);
    const subCol = pdfFindCol(cols, [/subtotal/]);
    const taxCol = pdfFindCol(cols, [/tax_amount/, /\btax\b/]);
    const discCol = pdfFindCol(cols, [/discount/]);
    const typeCol = pdfFindCol(cols, [/order_type/]);
    const methodCol = pdfFindCol(cols, [/payment_method/]);
    const modeCol = pdfFindCol(cols, [/collection_mode/, /payment_collection/]);
    const dateCol = pdfFindCol(cols, [/order_date/]);
    const typeCounts = pdfCountByCol(rows, typeCol);
    const statusCounts = pdfCountByCol(rows, statusCol);
    const methodCounts = pdfCountByCol(rows, methodCol);
    const modeCounts = pdfCountByCol(rows, modeCol);
    const revenue = pdfSumCol(rows, totalCol);
    const subtotal = pdfSumCol(rows, subCol);
    const tax = pdfSumCol(rows, taxCol);
    const discount = pdfSumCol(rows, discCol);
    const dineIn = typeCounts.find(([l]) => /dine/i.test(l))?.[1] || 0;
    const roomService = typeCounts.find(([l]) => /room/i.test(l))?.[1] || 0;
    const completed = statusCounts.find(([l]) => /complete/i.test(l))?.[1] || 0;

    return {
      kpis: [
        { label: 'TOTAL ORDERS', value: String(rows.length), accent: PDF_BRAND.chartTeal },
        { label: 'TOTAL REVENUE', value: pdfFormatMoney(revenue), accent: PDF_BRAND.chartGreen },
        { label: 'SUBTOTAL', value: pdfFormatMoney(subtotal), accent: PDF_BRAND.chartBlue },
        { label: 'TAX', value: pdfFormatMoney(tax), accent: PDF_BRAND.chartPurple },
        { label: 'DISCOUNTS', value: pdfFormatMoney(discount), accent: PDF_BRAND.chartOrange },
        { label: 'COMPLETED', value: String(completed), accent: PDF_BRAND.badgeCheckedOut },
        { label: 'DINE IN', value: String(dineIn), accent: PDF_BRAND.chartMint },
        { label: 'ROOM SERVICE', value: String(roomService), accent: PDF_BRAND.chartPink },
      ],
      footnote: 'KPIs are summed from this restaurant export only.',
      charts: {
        left: { title: 'Order Status', series: statusCounts },
        right: {
          title: methodCounts.length ? 'Payment Method' : typeCounts.length ? 'Order Type' : 'Collection Mode',
          series: methodCounts.length ? methodCounts : typeCounts.length ? typeCounts : modeCounts,
        },
      },
      meta: { kind, trend: pdfDayTrend(rows, dateCol) },
    };
  }

  if (kind === 'police') {
    const guestsCol = pdfFindCol(cols, [/no_of_guests/, /^guests$/]);
    const adultsCol = pdfFindCol(cols, [/^adults?$/]);
    const childrenCol = pdfFindCol(cols, [/^children$/, /^child$/]);
    const countryCol = pdfFindCol(cols, [/nationality/, /country/]);
    const idCol = pdfFindCol(cols, [/id_number/, /id number/]);
    const arrivalCol = pdfFindCol(cols, [/check_in/, /arrival/]);
    const roomCol = pdfFindCol(cols, [/room_number/, /^room$/]);
    let totalGuests = 0;
    let guestsKnown = 0;
    let foreign = 0;
    let missingId = 0;
    const rooms = new Set();
    rows.forEach((row) => {
      let n = 0;
      if (guestsCol) {
        const g = Number(row[guestsCol.key]);
        if (Number.isFinite(g) && g > 0) n += g;
      }
      if (!n && (adultsCol || childrenCol)) {
        const a = adultsCol ? Number(row[adultsCol.key]) : 0;
        const c = childrenCol ? Number(row[childrenCol.key]) : 0;
        n = (Number.isFinite(a) ? a : 0) + (Number.isFinite(c) ? c : 0);
      }
      if (n > 0) {
        totalGuests += n;
        guestsKnown += 1;
      }
      if (countryCol && !pdfIsBlank(row[countryCol.key])) {
        const cn = pdfNorm(row[countryCol.key]);
        if (cn && !/^(india|in|ind)$/.test(cn)) foreign += 1;
      }
      if (idCol && pdfIsBlank(row[idCol.key])) missingId += 1;
      if (roomCol && !pdfIsBlank(row[roomCol.key])) {
        String(row[roomCol.key])
          .split(/[,;/|]+/)
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((r) => rooms.add(r));
      }
    });
    const statusCounts = pdfCountByCol(rows, statusCol);
    const countryCounts = pdfCountByCol(rows, countryCol);
    const arrivalTrend = pdfDayTrend(rows, arrivalCol);
    let checkedIn = 0;
    let checkedOut = 0;
    rows.forEach((row) => {
      const sk = pdfStayKind(statusCol ? row[statusCol.key] : row.status);
      if (sk === 'checked_in') checkedIn += 1;
      if (sk === 'checked_out') checkedOut += 1;
    });

    return {
      kpis: [
        { label: 'TOTAL RECORDS', value: String(rows.length), accent: PDF_BRAND.chartTeal },
        {
          label: 'TOTAL GUESTS',
          value: guestsKnown ? String(totalGuests) : String(rows.length),
          accent: PDF_BRAND.chartBlue,
        },
        { label: 'CHECKED IN', value: String(checkedIn), accent: PDF_BRAND.badgeCheckedIn },
        { label: 'CHECKED OUT', value: String(checkedOut), accent: PDF_BRAND.badgeCheckedOut },
        { label: 'FOREIGN GUESTS', value: String(foreign), accent: PDF_BRAND.chartPurple },
        { label: 'MISSING ID', value: String(missingId), accent: PDF_BRAND.badgePending },
        { label: 'UNIQUE ROOMS', value: String(rooms.size || '—'), accent: PDF_BRAND.chartMint },
        {
          label: 'COUNTRIES',
          value: String(countryCounts.length || '—'),
          accent: PDF_BRAND.chartOrange,
        },
      ],
      footnote: 'Police log KPIs use guest, ID, nationality, and stay status fields from this export.',
      charts: {
        left: { title: 'Stay Status', series: statusCounts },
        right: arrivalTrend.length
          ? { title: 'Arrival Trend', series: arrivalTrend, type: 'vbar' }
          : { title: 'Nationality / Country', series: countryCounts },
      },
      meta: { kind },
    };
  }

  if (kind === 'finance') {
    const amountCol = pdfFindCol(cols, [/^amount$/, /total_amount/]);
    const methodCol = pdfFindCol(cols, [/payment_method/, /^method$/]);
    const typeCol = pdfFindCol(cols, [/payment_type/, /^type$/]);
    const dateCol = pdfFindCol(cols, [/payment_date/, /^date$/, /collected/]);
    const statusCounts = pdfCountByCol(rows, statusCol);
    const methodCounts = pdfCountByCol(rows, methodCol);
    const typeCounts = pdfCountByCol(rows, typeCol);
    const total = pdfSumCol(rows, amountCol);
    const captured = statusCounts
      .filter(([l]) => /captur|success|paid|complete/i.test(l))
      .reduce((s, [, n]) => s + n, 0);
    const failed = statusCounts
      .filter(([l]) => /fail|cancel|refund/i.test(l))
      .reduce((s, [, n]) => s + n, 0);
    const avg = rows.length ? total / rows.length : 0;

    return {
      kpis: [
        { label: 'PAYMENTS', value: String(rows.length), accent: PDF_BRAND.chartTeal },
        { label: 'TOTAL AMOUNT', value: pdfFormatMoney(total), accent: PDF_BRAND.chartGreen },
        { label: 'AVG PAYMENT', value: pdfFormatMoney(avg), accent: PDF_BRAND.chartBlue },
        { label: 'CAPTURED / PAID', value: String(captured), accent: PDF_BRAND.badgeCheckedOut },
        { label: 'FAILED / ALERTS', value: String(failed), accent: PDF_BRAND.badgeCancelled },
        {
          label: 'METHODS',
          value: String(methodCounts.length || '—'),
          accent: PDF_BRAND.chartPurple,
        },
        {
          label: 'TOP METHOD',
          value: methodCounts[0] ? String(methodCounts[0][0]).slice(0, 12) : '—',
          accent: PDF_BRAND.chartMint,
        },
        {
          label: 'TYPES',
          value: String(typeCounts.length || '—'),
          accent: PDF_BRAND.chartOrange,
        },
      ],
      footnote: 'Payment KPIs are aggregated from amount and status columns in this export.',
      charts: {
        left: { title: 'Payment Status', series: statusCounts },
        right: methodCounts.length
          ? { title: 'Payment Method', series: methodCounts }
          : { title: 'Payment Type', series: typeCounts },
      },
      meta: { kind, trend: pdfDayTrend(rows, dateCol) },
    };
  }

  if (kind === 'gst') {
    const invTotal = pdfFindCol(cols, [/invoice_total/]);
    const gstTotal = pdfFindCol(cols, [/total_gst/]);
    const roomRev = pdfFindCol(cols, [/room_revenue/]);
    const foodRev = pdfFindCol(cols, [/food_revenue/]);
    const taxable = pdfFindCol(cols, [/taxable_amount/]);
    const modeCol = pdfFindCol(cols, [/payment_mode/]);
    const statusCounts = pdfCountByCol(rows, statusCol);
    const modeCounts = pdfCountByCol(rows, modeCol);

    return {
      kpis: [
        { label: 'INVOICES', value: String(rows.length), accent: PDF_BRAND.chartTeal },
        { label: 'INVOICE TOTAL', value: pdfFormatMoney(pdfSumCol(rows, invTotal)), accent: PDF_BRAND.chartGreen },
        { label: 'TOTAL GST', value: pdfFormatMoney(pdfSumCol(rows, gstTotal)), accent: PDF_BRAND.chartOrange },
        { label: 'TAXABLE', value: pdfFormatMoney(pdfSumCol(rows, taxable)), accent: PDF_BRAND.chartBlue },
        { label: 'ROOM REVENUE', value: pdfFormatMoney(pdfSumCol(rows, roomRev)), accent: PDF_BRAND.chartPurple },
        { label: 'FOOD REVENUE', value: pdfFormatMoney(pdfSumCol(rows, foodRev)), accent: PDF_BRAND.chartPink },
        {
          label: 'PAY MODES',
          value: String(modeCounts.length || '—'),
          accent: PDF_BRAND.chartMint,
        },
        {
          label: 'STATUSES',
          value: String(statusCounts.length || '—'),
          accent: PDF_BRAND.badgeDefault,
        },
      ],
      footnote: 'GST KPIs sum revenue and tax columns from this export.',
      charts: {
        left: { title: 'Payment Status', series: statusCounts },
        right: { title: 'Payment Mode', series: modeCounts },
      },
      meta: { kind },
    };
  }

  if (kind === 'inventory') {
    const statusCounts = pdfCountByCol(rows, statusCol);
    const cleanCol = pdfFindCol(cols, [/clean_status/, /cleaning/]);
    const typeCol = pdfFindCol(cols, [/room_type/]);
    const availCol = pdfFindCol(cols, [/is_available/, /avail/]);
    const cleanCounts = pdfCountByCol(rows, cleanCol);
    const typeCounts = pdfCountByCol(rows, typeCol);
    let available = 0;
    rows.forEach((row) => {
      if (!availCol) return;
      const v = pdfNorm(row[availCol.key]);
      if (v === 'yes' || v === 'true' || v === '1' || v === 'available') available += 1;
    });

    return {
      kpis: [
        { label: 'ROOMS', value: String(rows.length), accent: PDF_BRAND.chartTeal },
        { label: 'AVAILABLE', value: String(available), accent: PDF_BRAND.badgeCheckedOut },
        {
          label: 'ROOM TYPES',
          value: String(typeCounts.length || '—'),
          accent: PDF_BRAND.chartBlue,
        },
        {
          label: 'TOP TYPE',
          value: typeCounts[0] ? String(typeCounts[0][0]).slice(0, 14) : '—',
          accent: PDF_BRAND.chartPurple,
        },
        {
          label: 'STATUS BANDS',
          value: String(statusCounts.length || '—'),
          accent: PDF_BRAND.chartMint,
        },
        {
          label: 'CLEANING',
          value: String(cleanCounts.length || '—'),
          accent: PDF_BRAND.chartOrange,
        },
        {
          label: 'TOP STATUS',
          value: statusCounts[0] ? String(statusCounts[0][0]).slice(0, 12) : '—',
          accent: PDF_BRAND.chartPink,
        },
        {
          label: 'TOP CLEAN',
          value: cleanCounts[0] ? String(cleanCounts[0][0]).slice(0, 12) : '—',
          accent: PDF_BRAND.badgeDefault,
        },
      ],
      footnote: 'Inventory KPIs reflect room status and availability in this export.',
      charts: {
        left: { title: 'Room Status', series: statusCounts },
        right: cleanCounts.length
          ? { title: 'Cleaning Status', series: cleanCounts }
          : { title: 'Room Types', series: typeCounts },
      },
      meta: { kind },
    };
  }

  if (kind === 'customer') {
    const spentCol = pdfFindCol(cols, [/total_spent/, /spent/]);
    const bookingsCol = pdfFindCol(cols, [/total_bookings/, /bookings/]);
    const statusCounts = pdfCountByCol(rows, statusCol);
    const totalSpent = pdfSumCol(rows, spentCol);
    const totalBookings = pdfSumCol(rows, bookingsCol);

    return {
      kpis: [
        { label: 'GUESTS', value: String(rows.length), accent: PDF_BRAND.chartTeal },
        { label: 'TOTAL SPENT', value: pdfFormatMoney(totalSpent), accent: PDF_BRAND.chartGreen },
        { label: 'TOTAL BOOKINGS', value: pdfFormatMoney(totalBookings), accent: PDF_BRAND.chartBlue },
        {
          label: 'AVG SPENT',
          value: rows.length ? pdfFormatMoney(totalSpent / rows.length) : '—',
          accent: PDF_BRAND.chartPurple,
        },
        {
          label: 'STATUSES',
          value: String(statusCounts.length || '—'),
          accent: PDF_BRAND.chartMint,
        },
        {
          label: 'TOP STATUS',
          value: statusCounts[0] ? String(statusCounts[0][0]).slice(0, 12) : '—',
          accent: PDF_BRAND.chartOrange,
        },
        { label: 'RECORDS', value: String(rows.length), accent: PDF_BRAND.chartPink },
        { label: 'EXPORT', value: 'OK', accent: PDF_BRAND.badgeCheckedOut },
      ],
      footnote: 'Guest KPIs use spent / booking totals from this customer export.',
      charts: {
        left: { title: 'Guest Status', series: statusCounts },
        right: { title: 'Status Snapshot', series: statusCounts },
      },
      meta: { kind },
    };
  }

  // —— booking (default) ——
  const guestsCol = pdfFindCol(cols, [/no_of_guests/, /guests?/, /\bpax\b/, /\badults\b/]);
  const nightsCol = pdfFindCol(cols, [/\bnights?\b/]);
  const arrivalCol = pdfFindCol(cols, [/check_in/, /arrival/]);
  const channelCol = pdfFindCol(cols, [/channel/]);
  const payStatusCol = pdfFindCol(cols, [/payment_status/, /pay_status/]);
  const roomCol = pdfFindCol(cols, [/\brooms?\b/]);
  const totalCol = pdfFindCol(cols, [/total_amount/, /^total$/]);
  let totalGuests = 0;
  let guestsKnown = 0;
  let nightsSum = 0;
  let nightsKnown = 0;
  let checkedIn = 0;
  let checkedOut = 0;
  let confirmed = 0;
  let cancelled = 0;
  const rooms = new Set();

  rows.forEach((row) => {
    const sk = pdfStayKind(statusCol ? row[statusCol.key] : row.status);
    if (sk === 'checked_in') checkedIn += 1;
    else if (sk === 'checked_out') checkedOut += 1;
    else if (sk === 'confirmed') confirmed += 1;
    else if (sk === 'cancelled' || sk === 'noshow') cancelled += 1;

    if (guestsCol) {
      const n = Number(row[guestsCol.key]);
      if (Number.isFinite(n) && n > 0) {
        totalGuests += n;
        guestsKnown += 1;
      }
    }
    if (nightsCol) {
      const n = Number(row[nightsCol.key]);
      if (Number.isFinite(n) && n > 0) {
        nightsSum += n;
        nightsKnown += 1;
      }
    }
    if (roomCol && !pdfIsBlank(row[roomCol.key])) {
      String(row[roomCol.key])
        .split(/[,;/|]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((r) => rooms.add(r));
    }
  });

  const statusCounts = pdfCountByCol(rows, statusCol);
  const channelCounts = pdfCountByCol(rows, channelCol);
  const payCounts = pdfCountByCol(rows, payStatusCol);
  const arrivalTrend = pdfDayTrend(rows, arrivalCol);
  const revenue = pdfSumCol(rows, totalCol);
  const avgStay = nightsKnown ? (nightsSum / nightsKnown).toFixed(1) : '—';

  return {
    kpis: [
      { label: 'TOTAL BOOKINGS', value: String(rows.length), accent: PDF_BRAND.chartTeal },
      {
        label: guestsKnown ? 'TOTAL GUESTS' : 'UNIQUE ROOMS',
        value: guestsKnown ? String(totalGuests) : String(rooms.size || '—'),
        accent: PDF_BRAND.chartBlue,
      },
      { label: 'CHECKED IN', value: String(checkedIn), accent: PDF_BRAND.badgeCheckedIn },
      { label: 'CHECKED OUT', value: String(checkedOut), accent: PDF_BRAND.badgeCheckedOut },
      { label: 'CONFIRMED', value: String(confirmed), accent: PDF_BRAND.badgeConfirmed },
      { label: 'AVG STAY', value: String(avgStay), accent: PDF_BRAND.chartPurple },
      { label: 'REVENUE', value: pdfFormatMoney(revenue), accent: PDF_BRAND.chartGreen },
      { label: 'CANCELLED', value: String(cancelled), accent: PDF_BRAND.badgeCancelled },
    ],
    footnote: 'Booking KPIs use stay status, nights, and totals from this export.',
    charts: {
      left: { title: 'Booking Status', series: statusCounts },
      right: arrivalTrend.length
        ? { title: 'Check-in Trend', series: arrivalTrend, type: 'vbar' }
        : channelCounts.length
          ? { title: 'Channel Mix', series: channelCounts }
          : { title: 'Payment Status', series: payCounts },
    },
    meta: { kind },
  };
}

function pdfDrawSectionTitle(doc, x, y, title, width) {
  doc.setFillColor(...PDF_BRAND.titleTeal);
  doc.roundedRect(x, y, width, 7, 1, 1, 'F');
  doc.setTextColor(...PDF_BRAND.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(String(title).toUpperCase(), x + 3, y + 4.7);
  return y + 10;
}

function pdfDrawKpiCards(doc, cards, x, y, width) {
  const gap = 2.5;
  const cols = Math.min(4, cards.length);
  const rows = Math.ceil(cards.length / cols);
  const cardW = (width - gap * (cols - 1)) / cols;
  const cardH = 18;

  cards.forEach((card, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = x + col * (cardW + gap);
    const cy = y + row * (cardH + gap);

    doc.setFillColor(...PDF_BRAND.cardBg);
    doc.setDrawColor(...PDF_BRAND.softBorder);
    doc.setLineWidth(0.35);
    doc.roundedRect(cx, cy, cardW, cardH, 2, 2, 'FD');
    doc.setFillColor(...card.accent);
    doc.roundedRect(cx, cy, 1.8, cardH, 0.5, 0.5, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(...PDF_BRAND.muted);
    doc.text(card.label, cx + 4, cy + 5.5);

    doc.setFontSize(13);
    doc.setTextColor(...PDF_BRAND.text);
    doc.text(String(card.value), cx + 4, cy + 13.5);
  });

  return y + rows * (cardH + gap) + 1;
}

function pdfDrawHBarChart(doc, title, series, x, y, width, height) {
  const panelH = height;
  doc.setFillColor(...PDF_BRAND.surface);
  doc.setDrawColor(...PDF_BRAND.softBorder);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, width, panelH, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...PDF_BRAND.titleTeal);
  doc.text(title, x + 3, y + 5);

  if (!series.length) {
    doc.setFontSize(7);
    doc.setTextColor(...PDF_BRAND.muted);
    doc.text('No data', x + 3, y + 14);
    return y + panelH;
  }

  const max = Math.max(...series.map((s) => s[1]), 1);
  const chartTop = y + 8;
  const chartH = panelH - 12;
  const rowH = Math.min(6.5, chartH / series.length);
  const labelW = Math.min(42, width * 0.38);
  const barMax = width - labelW - 18;

  series.forEach(([label, value], i) => {
    const ry = chartTop + i * rowH;
    const barW = (value / max) * barMax;
    const color = PDF_CHART_PALETTE[i % PDF_CHART_PALETTE.length];

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(...PDF_BRAND.text);
    const lbl = doc.splitTextToSize(String(label), labelW - 2)[0] || String(label);
    doc.text(lbl, x + 3, ry + 3.2);

    doc.setFillColor(...color);
    doc.roundedRect(x + labelW, ry + 0.6, Math.max(barW, 1.2), 3.6, 0.8, 0.8, 'F');

    doc.setFontSize(5.5);
    doc.setTextColor(...PDF_BRAND.muted);
    doc.text(String(value), x + labelW + Math.max(barW, 1.2) + 2, ry + 3.2);
  });

  return y + panelH;
}

function pdfDrawVBarChart(doc, title, series, x, y, width, height) {
  const panelH = height;
  doc.setFillColor(...PDF_BRAND.surface);
  doc.setDrawColor(...PDF_BRAND.softBorder);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, width, panelH, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...PDF_BRAND.titleTeal);
  doc.text(title, x + 3, y + 5);

  if (!series.length) {
    doc.setFontSize(7);
    doc.setTextColor(...PDF_BRAND.muted);
    doc.text('No data', x + 3, y + 14);
    return y + panelH;
  }

  const max = Math.max(...series.map((s) => s[1]), 1);
  const plotX = x + 6;
  const plotY = y + 9;
  const plotW = width - 12;
  const plotH = panelH - 20;
  const gap = 2;
  const barW = Math.max(4, (plotW - gap * (series.length - 1)) / series.length);

  // baseline
  doc.setDrawColor(...PDF_BRAND.softBorder);
  doc.setLineWidth(0.25);
  doc.line(plotX, plotY + plotH, plotX + plotW, plotY + plotH);

  series.forEach(([label, value], i) => {
    const h = (value / max) * (plotH - 2);
    const bx = plotX + i * (barW + gap);
    const by = plotY + plotH - h;
    const color = PDF_CHART_PALETTE[i % PDF_CHART_PALETTE.length];
    doc.setFillColor(...color);
    doc.roundedRect(bx, by, barW, Math.max(h, 1), 0.6, 0.6, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5);
    doc.setTextColor(...PDF_BRAND.muted);
    doc.text(String(value), bx + barW / 2, by - 1.2, { align: 'center' });

    const short =
      String(label).length > 8 ? String(label).slice(-8) : String(label);
    doc.setFontSize(4.5);
    doc.text(short, bx + barW / 2, plotY + plotH + 3.5, { align: 'center' });
  });

  return y + panelH;
}

/**
 * Executive BI-style RoomNexa PDF for Insights → Reports only.
 * Preserves all column data; adds KPI dashboard + charts above the table.
 *
 * @param {string} title
 * @param {string} subtitle
 * @param {{ key: string, label: string }[]} columns
 * @param {Record<string, unknown>[]} rows
 * @param {string} filename
 * @param {{
 *   landscape?: boolean,
 *   fontSize?: number,
 *   hotelName?: string,
 *   reportType?: string,
 *   hotelLogoUrl?: string,
 * }} [options]
 */
export async function downloadPremiumReportPdf(title, subtitle, columns, rows, filename, options = {}) {
  const {
    landscape = false,
    fontSize = 7,
    hotelName = '',
    reportType = '',
    hotelLogoUrl = '',
  } = options;
  const cols = Array.isArray(columns) ? columns : [];
  const dataRows = Array.isArray(rows) ? rows : [];

  const doc = new jsPDF({
    orientation: landscape ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 10;
  const contentW = pageW - marginX * 2;
  const footerH = 12;
  const contHeaderH = 16;

  const reportTitle = String(title || 'Report').trim();
  const hotel = String(hotelName || '').trim();
  const sub = String(subtitle || '').trim();
  const generated = new Date().toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const hotelLabel = (hotel || reportTitle).toUpperCase();
  const reportLabel = hotel ? reportTitle.toUpperCase() : 'REPORT';
  const mainHeading = hotel
    ? `${hotel.toUpperCase()}  —  ${reportTitle.toUpperCase()}`
    : reportTitle.toUpperCase();

  const analytics = computePdfAnalytics(cols, dataRows, reportType);
  const logo = await loadPdfImageDataUrl(hotelLogoUrl);

  // —— Header: white bg, logo left, hotel name black + centred ——
  const titleH = 26;
  const accentH = 1.5;
  doc.setFillColor(...PDF_BRAND.white);
  doc.rect(0, 0, pageW, titleH, 'F');
  // Match Summary section bar colour (titleTeal)
  doc.setFillColor(...PDF_BRAND.titleTeal);
  doc.rect(0, titleH, pageW, accentH, 'F');

  const logoBox = 14;
  const centerX = pageW / 2;
  if (logo) {
    let drawW = logoBox;
    let drawH = drawW * (logo.aspect || 1);
    if (drawH > logoBox) {
      drawH = logoBox;
      drawW = drawH / (logo.aspect || 1);
    }
    const logoX = marginX;
    const logoY = (titleH - drawH) / 2;
    doc.setDrawColor(...PDF_BRAND.softBorder);
    doc.setLineWidth(0.25);
    doc.setFillColor(...PDF_BRAND.white);
    doc.roundedRect(logoX - 0.5, logoY - 0.5, drawW + 1, drawH + 1, 1, 1, 'FD');
    try {
      doc.addImage(logo.dataUrl, logo.format, logoX, logoY, drawW, drawH);
    } catch {
      // ignore corrupt logo
    }
  }

  doc.setFont('helvetica', 'bold');
  const hotelSize = landscape ? 14 : 13;
  const reportSize = 8;
  const lineGap = 4.4;
  const blockTop = titleH / 2 - (hotel ? lineGap / 2 : 0);
  const titleMaxW = contentW - (logo ? logoBox + 8 : 0);

  if (hotel) {
    doc.setFontSize(hotelSize);
    doc.setTextColor(...PDF_BRAND.text);
    const hotelLine = doc.splitTextToSize(hotelLabel, titleMaxW)[0] || hotelLabel;
    doc.text(hotelLine, centerX, blockTop, { align: 'center', baseline: 'middle' });

    doc.setFontSize(reportSize);
    doc.setTextColor(...PDF_BRAND.titleTeal);
    doc.text(reportLabel, centerX, blockTop + lineGap, { align: 'center', baseline: 'middle' });
  } else {
    doc.setFontSize(hotelSize);
    doc.setTextColor(...PDF_BRAND.text);
    const titleLine = doc.splitTextToSize(reportLabel, titleMaxW)[0] || reportLabel;
    doc.text(titleLine, centerX, titleH / 2, { align: 'center', baseline: 'middle' });
  }

  let y = titleH + accentH + 3;

  // —— Period meta strip ——
  doc.setFillColor(...PDF_BRAND.cream);
  doc.roundedRect(marginX, y, contentW, 8, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...PDF_BRAND.titleTeal);
  const periodLine = sub || 'All dates';
  doc.text(
    doc.splitTextToSize(`PERIOD  ·  ${periodLine}`, contentW - 6)[0] || periodLine,
    marginX + 3,
    y + 5.2
  );
  y += 12;

  // —— Summary KPIs ——
  y = pdfDrawSectionTitle(doc, marginX, y, '1 · Summary', contentW);
  y = pdfDrawKpiCards(doc, analytics.kpis, marginX, y, contentW);
  if (analytics.footnote) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(5.5);
    doc.setTextColor(...PDF_BRAND.muted);
    doc.text(String(analytics.footnote), marginX, y);
    y += 4;
  } else {
    y += 1;
  }

  // —— Analytics charts (report-type specific) ——
  y = pdfDrawSectionTitle(doc, marginX, y, '2 · Analytics', contentW);
  const chartGap = 3;
  const chartW = (contentW - chartGap) / 2;
  const chartH = landscape ? 42 : 48;
  const leftChart = analytics.charts?.left || { title: 'Overview', series: [] };
  const rightChart = analytics.charts?.right || { title: 'Breakdown', series: [] };

  pdfDrawHBarChart(
    doc,
    leftChart.title,
    leftChart.series?.length ? leftChart.series : [['Records', dataRows.length]],
    marginX,
    y,
    chartW,
    chartH
  );

  if (rightChart.type === 'vbar') {
    pdfDrawVBarChart(
      doc,
      rightChart.title,
      rightChart.series || [],
      marginX + chartW + chartGap,
      y,
      chartW,
      chartH
    );
  } else {
    pdfDrawHBarChart(
      doc,
      rightChart.title,
      rightChart.series?.length ? rightChart.series : leftChart.series || [['Records', dataRows.length]],
      marginX + chartW + chartGap,
      y,
      chartW,
      chartH
    );
  }
  y += chartH + 5;

  // —— Detail table section ——
  y = pdfDrawSectionTitle(doc, marginX, y, '3 · Detailed Records', contentW);

  const statusColIdx = cols.findIndex((c) => /\bstatus\b/i.test(`${c.key} ${c.label}`));
  const rowFills = dataRows.map((row, idx) => {
    const statusFill = pdfRowFillFromData(row);
    if (statusFill) return statusFill;
    return idx % 2 === 1 ? PDF_BRAND.zebra : PDF_BRAND.zebraAlt;
  });
  const headFills = cols.map((c) => pdfHeaderFillForColumn(c));

  const head = [
    cols.map((c) => {
      const label = String(c.label || c.key || '').toUpperCase();
      return label;
    }),
  ];
  const body = dataRows.map((row) =>
    cols.map((c) => {
      const v = row[c.key];
      if (v === null || v === undefined) return '';
      const prefix = pdfIconPrefix(c);
      const s = String(v);
      // Highlight blanks for verification fields
      if (pdfIsBlank(s) && /\b(email|phone|id|address|nationality|country)\b/i.test(`${c.key} ${c.label}`)) {
        return `${prefix}—`;
      }
      return `${prefix}${s}`;
    })
  );
  if (!body.length) body.push(cols.map(() => ''));

  autoTable(doc, {
    startY: y,
    head,
    body,
    styles: {
      font: 'helvetica',
      fontStyle: 'bold',
      fontSize: Math.max(fontSize, 6),
      cellPadding: { top: 2.4, bottom: 2.4, left: 1.6, right: 1.6 },
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'center',
      textColor: PDF_BRAND.text,
      lineColor: PDF_BRAND.softBorder,
      lineWidth: 0.2,
      minCellHeight: 7.5,
    },
    headStyles: {
      fillColor: PDF_BRAND.titleTeal,
      textColor: PDF_BRAND.white,
      fontStyle: 'bold',
      fontSize: Math.min(Math.max(fontSize + 0.5, 6.5), 8),
      halign: 'center',
      valign: 'middle',
      cellPadding: { top: 2.8, bottom: 2.8, left: 1.4, right: 1.4 },
      lineColor: PDF_BRAND.white,
      lineWidth: 0.25,
    },
    bodyStyles: {
      fontStyle: 'bold',
      textColor: PDF_BRAND.text,
    },
    alternateRowStyles: {
      fillColor: PDF_BRAND.zebra,
    },
    columnStyles: Object.fromEntries(
      cols.map((c, i) => {
        const t = `${c.key || ''} ${c.label || ''}`.toLowerCase();
        const isName = /\b(name|guest|email|address|remark|note|booking)\b/.test(t);
        const isNum = /\b(amount|total|gst|tax|nights?|price|qty|count|spent|cur|guests?)\b/.test(t);
        const isStatus = /\bstatus\b/.test(t);
        return [
          i,
          {
            halign: isName ? 'left' : 'center',
            fontStyle: 'bold',
            minCellWidth: isStatus ? 20 : isNum ? 12 : undefined,
          },
        ];
      })
    ),
    margin: { left: marginX, right: marginX, top: contHeaderH + 2, bottom: footerH + 2 },
    tableLineColor: PDF_BRAND.softBorder,
    tableLineWidth: 0.2,
    showHead: 'everyPage',
    didParseCell: (data) => {
      if (data.section === 'head') {
        data.cell.styles.fillColor = headFills[data.column.index] || PDF_BRAND.titleTeal;
        data.cell.styles.textColor = PDF_BRAND.white;
        data.cell.styles.fontStyle = 'bold';
        return;
      }
      if (data.section !== 'body') return;

      const row = dataRows[data.row.index] || {};
      const col = cols[data.column.index];
      const fill = rowFills[data.row.index] || PDF_BRAND.rowDefault;
      data.cell.styles.fillColor = fill;
      data.cell.styles.fontStyle = 'bold';
      data.cell.styles.valign = 'middle';

      // Status badge cells
      if (data.column.index === statusColIdx) {
        const kind = pdfStatusKind(row.status ?? row[col?.key]);
        const badge = pdfStatusBadgeColors(kind);
        data.cell.styles.fillColor = badge.fill;
        data.cell.styles.textColor = badge.text;
        data.cell.styles.halign = 'center';
        return;
      }

      // Conditional formatting: missing critical fields
      const t = `${col?.key || ''} ${col?.label || ''}`.toLowerCase();
      const raw = row[col?.key];
      if (pdfIsBlank(raw) && /\b(email|phone|id_number|id number|address|nationality|country)\b/.test(t)) {
        data.cell.styles.fillColor = PDF_BRAND.warnMissing;
        data.cell.styles.textColor = [183, 110, 0];
      } else if (
        /\b(country|nationality)\b/.test(t) &&
        !pdfIsBlank(raw) &&
        !/^(india|in|ind)$/i.test(pdfNorm(raw))
      ) {
        data.cell.styles.fillColor = PDF_BRAND.warnForeign;
      } else if (/\bnights?\b/.test(t) && Number(raw) >= 7) {
        data.cell.styles.fillColor = PDF_BRAND.warnLongStay;
      }
    },
    didDrawPage: () => {
      const pageNumber = doc.internal.getNumberOfPages();

      if (pageNumber > 1) {
        doc.setFillColor(...PDF_BRAND.white);
        doc.rect(0, 0, pageW, 12, 'F');
        doc.setFillColor(...PDF_BRAND.titleTeal);
        doc.rect(0, 12, pageW, 1.5, 'F');
        doc.setTextColor(...PDF_BRAND.text);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(doc.splitTextToSize(mainHeading, contentW)[0] || reportTitle, pageW / 2, 6, {
          align: 'center',
        });
        doc.setFontSize(6.5);
        doc.setTextColor(...PDF_BRAND.titleTeal);
        doc.text('Detailed Records', pageW / 2, 10.5, { align: 'center' });
      }

      // Professional footer
      doc.setFillColor(...PDF_BRAND.cream);
      doc.rect(0, pageH - footerH, pageW, footerH, 'F');
      doc.setDrawColor(...PDF_BRAND.mint);
      doc.setLineWidth(0.7);
      doc.line(0, pageH - footerH, pageW, pageH - footerH);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...PDF_BRAND.titleTeal);
      doc.text('RoomNexa', marginX, pageH - 7.5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5.5);
      doc.setTextColor(...PDF_BRAND.muted);
      doc.text(
        `Generated ${generated}  ·  Confidential — for authorised hotel management use only`,
        marginX,
        pageH - 3.2
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...PDF_BRAND.titleTeal);
      doc.text(`Page ${pageNumber}`, pageW - marginX, pageH - 5, { align: 'right' });
    },
  });

  const name = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  doc.save(name);
}

/**
 * @param {{ name: string, columns: {key:string,label:string}[], rows: Record<string,unknown>[] }[]} sheets
 */
export function downloadExcelMultiSheet(sheets, filename) {
  const wb = XLSX.utils.book_new();
  sheets.forEach((s) => {
    const headers = s.columns.map((c) => c.label);
    const keys = s.columns.map((c) => c.key);
    const data = s.rows.map((row) => {
      const o = {};
      keys.forEach((k, i) => {
        const v = row[k];
        o[headers[i]] = v === null || v === undefined ? '' : v;
      });
      return o;
    });
    if (!data.length) {
      data.push(Object.fromEntries(headers.map((h) => [h, ''])));
    }
    const ws = XLSX.utils.json_to_sheet(data);
    const safeName = String(s.name || 'Sheet').slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, safeName);
  });
  const name = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, name);
}

/* -------------------------------------------------------------------------- */
/* Premium DSR-style Excel — Insights → Reports only (ExcelJS).               */
/* Colour-blocked sections, all-bold text, status row highlights.             */
/* Stock / POS keep using plain downloadExcel / downloadExcelMultiSheet.      */
/* -------------------------------------------------------------------------- */

/**
 * RoomNexa palette — teal header (#00897B), mint accent (#4CE1B6).
 */
const PREMIUM = {
  titleFill: '00897B',
  titleFont: 'FFFFFF',
  brandStrip: '4CE1B6',
  brandStripFont: '12151C',
  metaFill: 'E0F2F1',
  metaFont: '00695C',

  // Section group + column headers
  identityGroup: 'CFD8DC',
  identityHeader: '90A4AE',
  billingGroup: '4FC3F7',
  billingHeader: '29B6F6',
  billingBody: 'E1F5FE',
  paymentGroup: 'CE93D8',
  paymentHeader: '66BB6A',
  paymentBody: 'C8E6C9',
  remarkGroup: 'F48FB1',
  remarkHeader: 'F06292',
  remarkBody: 'FCE4EC',
  totalHeader: '78909C',
  totalBody: 'ECEFF1',

  // Full-row highlight flags (specific statuses / notes)
  rowComp: 'FFF59D',
  rowConfirmed: 'F8BBD0',
  rowCancelled: 'FFCDD2',
  rowNoShow: 'FFE0B2',
  rowCheckedIn: 'B2DFDB',
  rowCheckedOut: 'C8E6C9',
  rowPartialPay: 'FFE082',
  rowFailed: 'EF9A9A',
  rowDefault: 'FFFFFF',
  rowAlt: 'FAFAFA',

  footerFill: 'B2DFDB',
  footerFont: '00695C',
  border: '00897B',
  dataFont: '1A1A1A',
};

/**
 * Map columns into DSR colour bands without fragmenting guest/stay fields.
 * Loose keywords like "type" / "purpose" / "rooms" previously pushed mid-stay
 * columns into STATUS or BILLING, which repeated "GUEST / STAY" in the group row.
 *
 * @param {{ key: string, label: string }} col
 * @returns {'identity'|'billing'|'payment'|'remark'|'total'}
 */
function premiumColumnSection(col) {
  const key = String(col.key || '').toLowerCase();
  const label = String(col.label || '').toLowerCase();
  const t = `${key} ${label}`;

  // Explicit status / remarks only (keep this narrow so stay fields stay contiguous)
  if (
    key === 'status' ||
    key === 'payment_status' ||
    key === 'pay_status' ||
    key === 'clean_status' ||
    key === 'note' ||
    key === 'notes' ||
    key === 'remark' ||
    key === 'remarks' ||
    /^(pay )?status$/.test(label) ||
    /^payment status$/.test(label) ||
    /^cleaning$/.test(label) ||
    /^note$/.test(label) ||
    /^remarks?$/.test(label)
  ) {
    return 'remark';
  }

  // Totals
  if (
    key === 'total' ||
    key === 'total_amount' ||
    key === 'invoice_total' ||
    key === 'total_gst' ||
    key === 'total_spent' ||
    label === 'total' ||
    /\b(grand total|invoice total|payment total|bill total)\b/.test(t)
  ) {
    return 'total';
  }

  // Payments
  if (
    /\b(payment_method|payment_mode|payment_type|payment_date|payment_collection|reference_number|card|upi|cash)\b/.test(
      key
    ) ||
    /\b(payment method|payment mode|collected at|collection mode|reference)\b/.test(label) ||
    (/\b(amount|cur|currency)\b/.test(t) && !/\b(taxable|gst|cgst|sgst|igst|subtotal|discount|charge)\b/.test(t))
  ) {
    // currency next to totals still reads as money/payment band
    if (key === 'currency' || label === 'cur' || label === 'currency') return 'payment';
    if (/\b(payment|paid|card|upi|cash|advance|collected|collection|method|reference)\b/.test(t)) {
      return 'payment';
    }
  }
  if (key === 'amount' || label === 'amount') return 'payment';

  // Billing / revenue (not room numbers / room type)
  if (
    /\b(room_revenue|food_revenue|other_charges|taxable_amount|gst|cgst|sgst|igst|subtotal|discount_amount|tax_amount|additional_charges|nights)\b/.test(
      key
    ) ||
    /\b(room revenue|food revenue|other charges|taxable|gst|cgst|sgst|igst|subtotal|discount|tax amount|additional charges|nights|invoice no|invoice date)\b/.test(
      label
    )
  ) {
    return 'billing';
  }

  // Everything else is guest / stay / inventory identity (incl. purpose, ID docs, rooms list)
  return 'identity';
}

function sectionGroupLabel(section) {
  switch (section) {
    case 'billing':
      return 'BILLING';
    case 'payment':
      return 'PAYMENT';
    case 'remark':
      // Short label so single-column STATUS band is not clipped
      return 'STATUS';
    case 'total':
      return 'TOTAL';
    default:
      return 'STAY';
  }
}

function sectionGroupFill(section) {
  switch (section) {
    case 'billing':
      return PREMIUM.billingGroup;
    case 'payment':
      return PREMIUM.paymentGroup;
    case 'remark':
      return PREMIUM.remarkGroup;
    case 'total':
      return PREMIUM.totalHeader;
    default:
      return PREMIUM.identityGroup;
  }
}

function sectionHeaderFill(section) {
  switch (section) {
    case 'billing':
      return PREMIUM.billingHeader;
    case 'payment':
      return PREMIUM.paymentHeader;
    case 'remark':
      return PREMIUM.remarkHeader;
    case 'total':
      return PREMIUM.totalHeader;
    default:
      return PREMIUM.identityHeader;
  }
}

function sectionBodyFill(section, alt) {
  switch (section) {
    case 'billing':
      return PREMIUM.billingBody;
    case 'payment':
      return PREMIUM.paymentBody;
    case 'remark':
      return PREMIUM.remarkBody;
    case 'total':
      return PREMIUM.totalBody;
    default:
      return alt ? PREMIUM.rowAlt : PREMIUM.rowDefault;
  }
}

/**
 * Detect special row meaning from status / pay status / notes.
 * Returns a full-row highlight colour applied to every cell in the row.
 */
function detectRowHighlight(row) {
  const blob = [
    row.status,
    row.payment_status,
    row.pay_status,
    row.note,
    row.notes,
    row.remark,
    row.remarks,
    row.clean_status,
  ]
    .filter((v) => v != null && v !== '')
    .map((v) => String(v).toLowerCase().replace(/[_-]+/g, ' '))
    .join(' | ');

  if (!blob) return null;
  if (/\b(comp\.?|complimentary|comp room)\b/.test(blob)) return PREMIUM.rowComp;
  if (/\bcancel/.test(blob)) return PREMIUM.rowCancelled;
  if (/\bno.?show\b/.test(blob)) return PREMIUM.rowNoShow;
  if (/\b(failed|refund)\b/.test(blob)) return PREMIUM.rowFailed;
  if (/\bpartial/.test(blob) || /\bdue\b/.test(blob)) return PREMIUM.rowPartialPay;
  if (/\bchecked.?out\b/.test(blob) || /\bcompleted\b/.test(blob)) return PREMIUM.rowCheckedOut;
  if (/\bchecked.?in\b/.test(blob)) return PREMIUM.rowCheckedIn;
  if (/\bconfirmed\b/.test(blob)) return PREMIUM.rowConfirmed;
  return null;
}

function isLikelyNumericCell(value, col) {
  if (typeof value === 'number' && Number.isFinite(value)) return true;
  const t = `${col.key || ''} ${col.label || ''}`.toLowerCase();
  if (
    /\b(amount|total|gst|tax|price|rate|pax|rooms?|nights?|qty|quantity|count|paid|due|bill|spent)\b/.test(
      t
    )
  ) {
    return true;
  }
  if (typeof value === 'string' && value.trim() !== '' && /^-?\d+([.,]\d+)?$/.test(value.trim())) {
    return true;
  }
  return false;
}

function cellDisplayValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value;
  return value;
}

function gridBorder(strength = 'normal') {
  const style = strength === 'strong' ? 'medium' : 'thin';
  const edge = { style, color: { argb: `FF${PREMIUM.border}` } };
  return { top: edge, left: edge, bottom: edge, right: edge };
}

function styleCell(cell, { fill, bold = true, italic = false, size = 10, align = 'center', wrap = true }) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: `FF${fill}` },
  };
  cell.font = {
    name: 'Calibri',
    size,
    bold,
    italic,
    color: { argb: `FF${PREMIUM.dataFont}` },
  };
  cell.alignment = {
    horizontal: align,
    vertical: 'middle',
    wrapText: wrap,
    shrinkToFit: !wrap,
  };
  cell.border = gridBorder(bold && size >= 11 ? 'strong' : 'normal');
}

function triggerXlsxDownload(buffer, filename) {
  const name = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Build contiguous section spans for merged group headers. */
function buildSectionSpans(sections) {
  const spans = [];
  let i = 0;
  while (i < sections.length) {
    const section = sections[i];
    let j = i + 1;
    while (j < sections.length && sections[j] === section) j += 1;
    spans.push({ section, start: i + 1, end: j });
    i = j;
  }
  return spans;
}

/**
 * Premium colourful DSR-style Excel for Insights → Reports.
 * - Yellow title banner + RoomNexa mint strip
 * - Merged colour group headers (Guest / Billing / Payment / Remarks)
 * - Colour-blocked body columns
 * - All text bold for visibility
 * - Full-row colour for Comp / Cancelled / No-show / Checked-in / Checked-out / Partial / Failed
 *
 * @param {{ key: string, label: string }[]} columns
 * @param {Record<string, unknown>[]} rows
 * @param {string} filename
 * @param {{
 *   sheetName?: string,
 *   title?: string,
 *   subtitle?: string,
 *   generatedLabel?: string,
 * }} [meta]
 */
export async function downloadPremiumReportExcel(columns, rows, filename, meta = {}) {
  const ExcelJS = (await import('exceljs')).default;
  const cols = Array.isArray(columns) ? columns : [];
  const dataRows = Array.isArray(rows) ? rows : [];
  const colCount = Math.max(cols.length, 1);
  const sections = cols.map((c) => premiumColumnSection(c));
  const spans = buildSectionSpans(sections);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'RoomNexa';
  workbook.created = new Date();
  const sheetName = String(meta.sheetName || 'Report').slice(0, 31);
  const ws = workbook.addWorksheet(sheetName, {
    properties: { defaultRowHeight: 20 },
  });

  const title =
    (meta.title && String(meta.title).trim()) || sheetName || 'RoomNexa Report';
  const subtitle = meta.subtitle ? String(meta.subtitle).trim() : '';
  const generated =
    meta.generatedLabel ||
    `Generated ${new Date().toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })}`;

  // —— Row 1: RoomNexa teal title ——
  ws.mergeCells(1, 1, 1, colCount);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = title.toUpperCase();
  styleCell(titleCell, {
    fill: PREMIUM.titleFill,
    bold: true,
    size: 24,
    align: 'center',
  });
  titleCell.font = {
    name: 'Calibri',
    size: 24,
    bold: true,
    color: { argb: `FF${PREMIUM.titleFont}` },
  };
  titleCell.border = gridBorder('strong');
  ws.getRow(1).height = 42;
  for (let c = 2; c <= colCount; c += 1) {
    const cell = ws.getCell(1, c);
    styleCell(cell, { fill: PREMIUM.titleFill, bold: true, size: 24 });
    cell.font = {
      name: 'Calibri',
      size: 24,
      bold: true,
      color: { argb: `FF${PREMIUM.titleFont}` },
    };
    cell.border = gridBorder('strong');
  }

  // —— Row 2: RoomNexa mint brand strip ——
  ws.mergeCells(2, 1, 2, colCount);
  const brandCell = ws.getCell(2, 1);
  brandCell.value = 'RoomNexa  ·  Executive Daily Report';
  styleCell(brandCell, {
    fill: PREMIUM.brandStrip,
    bold: true,
    size: 12,
    align: 'center',
  });
  brandCell.font = {
    name: 'Calibri',
    size: 12,
    bold: true,
    color: { argb: `FF${PREMIUM.brandStripFont}` },
  };
  brandCell.border = gridBorder('strong');
  ws.getRow(2).height = 22;
  for (let c = 2; c <= colCount; c += 1) {
    const cell = ws.getCell(2, c);
    styleCell(cell, { fill: PREMIUM.brandStrip, bold: true, size: 12 });
    cell.border = gridBorder('strong');
  }

  // —— Row 3: period meta (bold + italic) ——
  ws.mergeCells(3, 1, 3, colCount);
  const metaCell = ws.getCell(3, 1);
  metaCell.value = [subtitle, generated].filter(Boolean).join('  ·  ') || generated;
  styleCell(metaCell, {
    fill: PREMIUM.metaFill,
    bold: true,
    italic: true,
    size: 12,
    align: 'center',
  });
  metaCell.font = {
    name: 'Calibri',
    size: 12,
    bold: true,
    italic: true,
    color: { argb: `FF${PREMIUM.metaFont}` },
  };
  metaCell.border = gridBorder('strong');
  ws.getRow(3).height = 24;
  for (let c = 2; c <= colCount; c += 1) {
    const cell = ws.getCell(3, c);
    styleCell(cell, { fill: PREMIUM.metaFill, bold: true, italic: true, size: 12 });
    cell.font = {
      name: 'Calibri',
      size: 12,
      bold: true,
      italic: true,
      color: { argb: `FF${PREMIUM.metaFont}` },
    };
    cell.border = gridBorder('strong');
  }

  // —— Row 4: merged section group headers (no repeated labels) ——
  const groupRow = ws.getRow(4);
  groupRow.height = 26;
  const seenGroupLabels = new Set();
  spans.forEach(({ section, start, end }) => {
    if (end > start) {
      ws.mergeCells(4, start, 4, end);
    }
    const cell = ws.getCell(4, start);
    const label = sectionGroupLabel(section);
    // Only print each band name once; later fragments keep colour but stay blank
    // so "STAY" / "STATUS" do not repeat across the row.
    if (seenGroupLabels.has(section)) {
      cell.value = '';
    } else {
      cell.value = label;
      seenGroupLabels.add(section);
    }
    styleCell(cell, {
      fill: sectionGroupFill(section),
      bold: true,
      size: 14,
      align: 'center',
      wrap: false,
    });
    cell.border = gridBorder('strong');
    for (let c = start + 1; c <= end; c += 1) {
      const filler = ws.getCell(4, c);
      styleCell(filler, {
        fill: sectionGroupFill(section),
        bold: true,
        size: 14,
        wrap: false,
      });
      filler.border = gridBorder('strong');
    }
  });
  if (!cols.length) {
    const cell = ws.getCell(4, 1);
    cell.value = 'STAY';
    styleCell(cell, { fill: PREMIUM.identityGroup, bold: true, size: 14, wrap: false });
  }

  // —— Row 5: column headers ——
  const headerRow = ws.getRow(5);
  headerRow.height = 30;
  cols.forEach((col, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = String(col.label || col.key || '').toUpperCase();
    styleCell(cell, {
      fill: sectionHeaderFill(sections[idx]),
      bold: true,
      size: 12,
      align: 'center',
      wrap: false,
    });
    cell.border = gridBorder('strong');
  });
  if (!cols.length) {
    const cell = headerRow.getCell(1);
    cell.value = 'NO COLUMNS';
    styleCell(cell, { fill: PREMIUM.identityHeader, bold: true, size: 12, wrap: false });
  }

  // Freeze below column headers
  ws.views = [{ state: 'frozen', ySplit: 5 }];

  // —— Data rows ——
  const sourceRows = dataRows.length
    ? dataRows
    : [Object.fromEntries(cols.map((c) => [c.key, '']))];

  sourceRows.forEach((row, rIdx) => {
    const excelRow = ws.getRow(6 + rIdx);
    excelRow.height = 22;
    const alt = rIdx % 2 === 1;
    // One colour for the whole row when status maps to a highlight
    const highlight = detectRowHighlight(row);

    cols.forEach((col, cIdx) => {
      const cell = excelRow.getCell(cIdx + 1);
      const raw = row[col.key];
      const section = sections[cIdx];
      const fill = highlight || sectionBodyFill(section, alt);
      const nameLike = /\b(name|guest|remark|note|address|email|booking)\b/i.test(
        `${col.key} ${col.label}`
      );

      cell.value = cellDisplayValue(raw);
      styleCell(cell, {
        fill,
        bold: true,
        size: 10,
        align: nameLike ? 'left' : 'center',
        wrap: false,
      });
      // Force same fill after styleCell (status row colour on every column)
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: `FF${fill}` },
      };
      cell.border = gridBorder('normal');

      if (typeof raw === 'number' && Number.isFinite(raw)) {
        cell.numFmt = Number.isInteger(raw) ? '#,##0' : '#,##0.00';
      }
    });
  });

  // Column widths — keep STATUS / remark columns wide enough to not clip
  cols.forEach((col, idx) => {
    const labelLen = String(col.label || col.key || '').length;
    let maxLen = labelLen;
    sourceRows.slice(0, 100).forEach((row) => {
      const v = row[col.key];
      const s = v === null || v === undefined ? '' : String(v);
      if (s.length > maxLen) maxLen = s.length;
    });
    const isRemark = sections[idx] === 'remark';
    const isStatusCol = /status/i.test(`${col.key} ${col.label}`);
    const minW = isStatusCol ? 18 : isRemark ? 16 : 12;
    ws.getColumn(idx + 1).width = Math.min(42, Math.max(minW, Math.ceil(maxLen * 1.15) + 4));
  });
  if (!cols.length) ws.getColumn(1).width = 24;

  // —— Footer totals ——
  if (dataRows.length) {
    const footerIdx = 6 + sourceRows.length;
    const footer = ws.getRow(footerIdx);
    footer.height = 22;
    cols.forEach((col, cIdx) => {
      const cell = footer.getCell(cIdx + 1);
      const section = sections[cIdx];
      styleCell(cell, {
        fill: PREMIUM.footerFill,
        bold: true,
        size: 10,
        align: 'center',
      });
      cell.font = {
        name: 'Calibri',
        size: 10,
        bold: true,
        color: { argb: `FF${PREMIUM.footerFont}` },
      };
      cell.border = gridBorder('strong');

      if (cIdx === 0) {
        cell.value = `TOTAL ROWS: ${dataRows.length}`;
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
        return;
      }

      const numericValues = dataRows
        .map((r) => r[col.key])
        .filter((v) => typeof v === 'number' && Number.isFinite(v));
      if (
        numericValues.length &&
        (section === 'billing' || section === 'payment' || section === 'total')
      ) {
        const sum = numericValues.reduce((a, b) => a + b, 0);
        cell.value = sum;
        cell.numFmt = Number.isInteger(sum) ? '#,##0' : '#,##0.00';
      } else {
        cell.value = '';
      }
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  triggerXlsxDownload(buffer, filename);
}

/* -------------------------------------------------------------------------- */
/* Police Report Excel — Booking → Room → Guest with merged cells + flat sheet */
/* -------------------------------------------------------------------------- */

const POLICE_BOOKING_MERGE_KEYS = new Set([
  'booking_code',
  'booking_date',
  'check_in',
  'check_out',
  'booking_source',
  'status',
  'purpose_of_stay',
]);

const POLICE_ROOM_MERGE_KEYS = new Set(['room_number', 'adults', 'children']);

const POLICE_HEADER_FILL = '00897B';
const POLICE_HEADER_FONT = 'FFFFFF';
const POLICE_ROW_ALT = 'F0FDFA';
const POLICE_ROW_DEFAULT = 'FFFFFF';
const POLICE_BORDER = '00897B';

/**
 * Compute 0-based inclusive merge ranges from police guest rows.
 * @param {Record<string, unknown>[]} rows
 */
export function buildPoliceMergeRangesFromRows(rows) {
  const booking = [];
  const room = [];
  if (!Array.isArray(rows) || !rows.length) return { booking, room };

  const flush = (list, start, end, fields) => {
    if (end > start) list.push({ start, end, fields });
  };

  let bStart = 0;
  let bKey = rows[0].booking_merge_key ?? rows[0].booking_code;
  let rStart = 0;
  let rKey = `${bKey}::${rows[0].room_merge_key ?? rows[0].room_number}`;

  for (let i = 1; i < rows.length; i += 1) {
    const row = rows[i];
    const curB = row.booking_merge_key ?? row.booking_code;
    const curR = `${curB}::${row.room_merge_key ?? row.room_number}`;
    if (curR !== rKey) {
      flush(room, rStart, i - 1, [...POLICE_ROOM_MERGE_KEYS]);
      rStart = i;
      rKey = curR;
    }
    if (curB !== bKey) {
      flush(booking, bStart, i - 1, [...POLICE_BOOKING_MERGE_KEYS]);
      bStart = i;
      bKey = curB;
    }
  }
  flush(room, rStart, rows.length - 1, [...POLICE_ROOM_MERGE_KEYS]);
  flush(booking, bStart, rows.length - 1, [...POLICE_BOOKING_MERGE_KEYS]);
  return { booking, room };
}

function policeGridBorder() {
  const edge = { style: 'thin', color: { argb: `FF${POLICE_BORDER}` } };
  return { top: edge, left: edge, bottom: edge, right: edge };
}

function stylePoliceCell(cell, { fill, bold = false, align = 'center', wrap = true } = {}) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: `FF${fill}` },
  };
  cell.font = {
    name: 'Calibri',
    size: bold ? 11 : 10,
    bold,
    color: { argb: bold && fill === POLICE_HEADER_FILL ? `FF${POLICE_HEADER_FONT}` : 'FF1A1A1A' },
  };
  cell.alignment = {
    horizontal: align,
    vertical: 'middle',
    wrapText: wrap,
  };
  cell.border = policeGridBorder();
}

function applyPoliceColumnWidths(ws, columns, rows) {
  columns.forEach((col, idx) => {
    const labelLen = String(col.label || col.key || '').length;
    let maxLen = labelLen;
    rows.slice(0, 200).forEach((row) => {
      const v = row[col.key];
      const s = v == null ? '' : String(v);
      if (s.length > maxLen) maxLen = s.length;
    });
    const isWide = /address|purpose|email|name/i.test(`${col.key} ${col.label}`);
    const maxW = isWide ? 36 : 22;
    const minW = isWide ? 16 : 10;
    ws.getColumn(idx + 1).width = Math.min(maxW, Math.max(minW, Math.ceil(maxLen * 1.05) + 2));
  });
}

function applyPolicePrintSetup(ws, headerRowNumber) {
  ws.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    paperSize: 9,
    margins: {
      left: 0.4,
      right: 0.4,
      top: 0.5,
      bottom: 0.5,
      header: 0.2,
      footer: 0.2,
    },
  };
  ws.pageSetup.printTitlesRow = `${headerRowNumber}:${headerRowNumber}`;
  ws.views = [{ state: 'frozen', ySplit: headerRowNumber, showGridLines: true }];
}

/**
 * Build Police Report (merged hierarchy) + Guest Data (flat) workbook and download.
 *
 * @param {{ key: string, label: string }[]} columns
 * @param {Record<string, unknown>[]} rows
 * @param {string} filename
 * @param {{
 *   title?: string,
 *   subtitle?: string,
 *   mergeRanges?: { booking?: Array<{start:number,end:number,fields?:string[]}>, room?: Array<{start:number,end:number,fields?:string[]}> },
 * }} [meta]
 */
export async function downloadPoliceReportExcel(columns, rows, filename, meta = {}) {
  const ExcelJS = (await import('exceljs')).default;
  const cols = Array.isArray(columns) ? columns : [];
  const dataRows = Array.isArray(rows) ? rows : [];
  const mergeRanges =
    meta.mergeRanges && (meta.mergeRanges.booking || meta.mergeRanges.room)
      ? {
          booking: meta.mergeRanges.booking || [],
          room: meta.mergeRanges.room || [],
        }
      : buildPoliceMergeRangesFromRows(dataRows);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'RoomNexa';
  workbook.created = new Date();

  const title = (meta.title && String(meta.title).trim()) || 'Police Report';
  const subtitle = meta.subtitle ? String(meta.subtitle).trim() : '';

  // —— Sheet 1: Police Report (merged) ——
  const ws = workbook.addWorksheet('Police Report', {
    properties: { defaultRowHeight: 18 },
  });

  ws.mergeCells(1, 1, 1, Math.max(cols.length, 1));
  const titleCell = ws.getCell(1, 1);
  titleCell.value = title;
  stylePoliceCell(titleCell, { fill: POLICE_HEADER_FILL, bold: true, align: 'center' });
  titleCell.font = {
    name: 'Calibri',
    size: 16,
    bold: true,
    color: { argb: `FF${POLICE_HEADER_FONT}` },
  };
  ws.getRow(1).height = 28;

  ws.mergeCells(2, 1, 2, Math.max(cols.length, 1));
  const subCell = ws.getCell(2, 1);
  subCell.value = subtitle || 'Booking → Room → Guest';
  stylePoliceCell(subCell, { fill: 'E0F2F1', bold: true, align: 'center' });
  ws.getRow(2).height = 20;

  const headerRowNum = 3;
  const headerRow = ws.getRow(headerRowNum);
  headerRow.height = 24;
  cols.forEach((col, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = String(col.label || col.key || '');
    stylePoliceCell(cell, { fill: POLICE_HEADER_FILL, bold: true, align: 'center', wrap: true });
  });

  const sourceRows = dataRows.length
    ? dataRows
    : [Object.fromEntries(cols.map((c) => [c.key, '']))];

  sourceRows.forEach((row, rIdx) => {
    const excelRow = ws.getRow(headerRowNum + 1 + rIdx);
    excelRow.height = 20;
    const alt = rIdx % 2 === 1;
    cols.forEach((col, cIdx) => {
      const cell = excelRow.getCell(cIdx + 1);
      const raw = row[col.key];
      const leftish = /name|address|purpose|email|source/i.test(`${col.key} ${col.label}`);
      cell.value = raw == null || raw === undefined ? '' : raw;
      stylePoliceCell(cell, {
        fill: alt ? POLICE_ROW_ALT : POLICE_ROW_DEFAULT,
        bold: false,
        align: leftish ? 'left' : 'center',
        wrap: true,
      });
    });
  });

  const keyToCol = new Map(cols.map((c, i) => [c.key, i + 1]));
  const dataStart = headerRowNum + 1;

  const applyMerges = (ranges, allowedKeys) => {
    (ranges || []).forEach((range) => {
      const start = Number(range.start);
      const end = Number(range.end);
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return;
      const fields = Array.isArray(range.fields) && range.fields.length
        ? range.fields
        : [...allowedKeys];
      fields.forEach((field) => {
        if (!allowedKeys.has(field)) return;
        const colIdx = keyToCol.get(field);
        if (!colIdx) return;
        const r1 = dataStart + start;
        const r2 = dataStart + end;
        try {
          ws.mergeCells(r1, colIdx, r2, colIdx);
          const cell = ws.getCell(r1, colIdx);
          cell.alignment = {
            horizontal: cell.alignment?.horizontal || 'center',
            vertical: 'middle',
            wrapText: true,
          };
        } catch {
          // Ignore overlapping / already-merged cells
        }
      });
    });
  };

  applyMerges(mergeRanges.booking, POLICE_BOOKING_MERGE_KEYS);
  applyMerges(mergeRanges.room, POLICE_ROOM_MERGE_KEYS);

  applyPoliceColumnWidths(ws, cols, sourceRows);
  applyPolicePrintSetup(ws, headerRowNum);

  // —— Sheet 2: Guest Data (flat, no merges) ——
  const wsFlat = workbook.addWorksheet('Guest Data', {
    properties: { defaultRowHeight: 18 },
  });
  const flatHeader = wsFlat.getRow(1);
  flatHeader.height = 24;
  cols.forEach((col, idx) => {
    const cell = flatHeader.getCell(idx + 1);
    cell.value = String(col.label || col.key || '');
    stylePoliceCell(cell, { fill: POLICE_HEADER_FILL, bold: true, align: 'center', wrap: true });
  });
  sourceRows.forEach((row, rIdx) => {
    const excelRow = wsFlat.getRow(2 + rIdx);
    const alt = rIdx % 2 === 1;
    cols.forEach((col, cIdx) => {
      const cell = excelRow.getCell(cIdx + 1);
      const raw = row[col.key];
      const leftish = /name|address|purpose|email|source/i.test(`${col.key} ${col.label}`);
      cell.value = raw == null || raw === undefined ? '' : raw;
      stylePoliceCell(cell, {
        fill: alt ? POLICE_ROW_ALT : POLICE_ROW_DEFAULT,
        bold: false,
        align: leftish ? 'left' : 'center',
        wrap: true,
      });
    });
  });
  applyPoliceColumnWidths(wsFlat, cols, sourceRows);
  applyPolicePrintSetup(wsFlat, 1);

  const buffer = await workbook.xlsx.writeBuffer();
  triggerXlsxDownload(buffer, filename);
}

/**
 * Police register PDF — matches Excel Police Report columns/layout.
 * Compact landscape table: small text, no icon prefixes, no dashboard KPIs.
 *
 * @param {{ key: string, label: string }[]} columns
 * @param {Record<string, unknown>[]} rows
 * @param {string} filename
 * @param {{ title?: string, subtitle?: string, hotelName?: string }} [meta]
 */
export async function downloadPoliceReportPdf(columns, rows, filename, meta = {}) {
  const cols = Array.isArray(columns) ? columns : [];
  const dataRows = Array.isArray(rows) ? rows : [];
  const title =
    (meta.title && String(meta.title).trim()) ||
    `${meta.hotelName ? `${meta.hotelName} — ` : ''}Police Report`;
  const subtitle = meta.subtitle ? String(meta.subtitle).trim() : '';

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 6;
  const footerH = 8;
  const contentW = pageW - marginX * 2;

  // Title bar (aligned with Excel Police Report sheet)
  doc.setFillColor(...PDF_BRAND.titleTeal);
  doc.rect(0, 0, pageW, 12, 'F');
  doc.setTextColor(...PDF_BRAND.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(doc.splitTextToSize(title, contentW)[0] || 'Police Report', pageW / 2, 5.5, {
    align: 'center',
  });
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Booking → Room → Guest', pageW / 2, 10, { align: 'center' });

  let y = 15;
  if (subtitle) {
    doc.setFillColor(...PDF_BRAND.cream);
    doc.roundedRect(marginX, y, contentW, 6, 1, 1, 'F');
    doc.setTextColor(...PDF_BRAND.titleTeal);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.text(doc.splitTextToSize(subtitle, contentW - 4)[0] || subtitle, marginX + 2, y + 4);
    y += 8;
  }

  const fontSize = 4.5;
  const padX = 1.0; // mm horizontal padding allowance inside cell

  const head = [cols.map((c) => String(c.label || c.key || ''))];
  const body = dataRows.length
    ? dataRows.map((row) =>
        cols.map((c) => {
          const v = row[c.key];
          if (v === null || v === undefined) return '';
          const s = String(v).trim();
          return s === '—' ? '-' : s;
        })
      )
    : [cols.map(() => '')];

  // Content-aware widths: fit short columns to max text; wrap long ones.
  const FIXED_FIT_KEYS = new Set([
    'sr',
    'adults',
    'children',
    'room_number',
    'gender',
    'status',
    'nationality',
    'date_of_birth',
    'purpose_of_stay',
    'phone',
    'id_document_type',
    'booking_source',
  ]);
  const WRAP_KEYS = new Set([
    'address',
    'guest_name',
    'email',
    'booking_code',
    'booking_date',
    'check_in',
    'check_out',
    'id_number',
  ]);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(fontSize);
  const measure = (text) => {
    const s = String(text ?? '');
    if (!s) return 0;
    return doc.getTextWidth(s) + padX;
  };

  const idealWidths = cols.map((c, colIdx) => {
    const headerLabel = String(c.label || c.key || '');
    let maxContent = measure(headerLabel);
    // Header often wraps; use ~half label length as min when multiline headers allowed
    doc.setFont('helvetica', 'bold');
    maxContent = Math.max(maxContent, measure(headerLabel));
    doc.setFont('helvetica', 'normal');

    for (let r = 0; r < body.length; r += 1) {
      maxContent = Math.max(maxContent, measure(body[r][colIdx]));
    }

    const key = c.key;
    let minW = 6;
    let maxW = 40;
    if (key === 'sr') {
      minW = 5;
      maxW = 8;
    } else if (key === 'adults' || key === 'children') {
      minW = 6;
      maxW = 9;
    } else if (key === 'room_number') {
      minW = 7;
      maxW = 12;
    } else if (key === 'gender') {
      minW = 8;
      maxW = 12;
    } else if (key === 'status') {
      // Fit longest status text (e.g. "Checked Out") — no truncation
      minW = 12;
      maxW = 22;
    } else if (key === 'nationality' || key === 'purpose_of_stay') {
      minW = 10;
      maxW = 18;
    } else if (key === 'date_of_birth') {
      minW = 12;
      maxW = 16;
    } else if (key === 'phone') {
      minW = 14;
      maxW = 18;
    } else if (key === 'id_document_type') {
      minW = 12;
      maxW = 18;
    } else if (key === 'booking_source') {
      minW = 12;
      maxW = 20;
    } else if (key === 'booking_date' || key === 'check_in' || key === 'check_out') {
      minW = 16;
      maxW = 24;
    } else if (key === 'booking_code') {
      minW = 16;
      maxW = 28;
    } else if (key === 'guest_name') {
      minW = 14;
      maxW = 28;
    } else if (key === 'id_number') {
      minW = 14;
      maxW = 22;
    } else if (key === 'email') {
      minW = 12;
      maxW = 26;
    } else if (key === 'address') {
      minW = 18;
      maxW = 42;
    }

    if (FIXED_FIT_KEYS.has(key)) {
      // Exact fit to content (clamped), so Status shows full "Checked Out"
      return Math.min(maxW, Math.max(minW, maxContent + 0.6));
    }
    // Wrappable columns: start from content preference but allow shrink + multiline
    return Math.min(maxW, Math.max(minW, Math.min(maxContent + 0.6, maxW)));
  });

  let totalIdeal = idealWidths.reduce((a, b) => a + b, 0);
  let widths = [...idealWidths];
  if (totalIdeal > contentW && totalIdeal > 0) {
    // Keep fixed-fit columns at ideal; shrink only wrap/flexible columns.
    const fixedSum = cols.reduce(
      (sum, c, i) => (FIXED_FIT_KEYS.has(c.key) ? sum + idealWidths[i] : sum),
      0
    );
    const flexIdx = cols
      .map((c, i) => i)
      .filter((i) => !FIXED_FIT_KEYS.has(cols[i].key));
    const flexBudget = Math.max(contentW - fixedSum, flexIdx.length * 8);
    const flexIdeal = flexIdx.reduce((s, i) => s + idealWidths[i], 0) || 1;
    widths = idealWidths.map((w, i) => {
      if (FIXED_FIT_KEYS.has(cols[i].key)) return w;
      return Math.max(8, (w / flexIdeal) * flexBudget);
    });
    // Final scale if still over (rare)
    const sum = widths.reduce((a, b) => a + b, 0);
    if (sum > contentW) {
      const scale = contentW / sum;
      widths = widths.map((w) => w * scale);
    }
  } else if (totalIdeal < contentW && totalIdeal > 0) {
    // Give leftover width to address / guest name / booking code
    let leftover = contentW - totalIdeal;
    const growOrder = ['address', 'guest_name', 'booking_code', 'email', 'id_number'];
    growOrder.forEach((key) => {
      if (leftover <= 0) return;
      const idx = cols.findIndex((c) => c.key === key);
      if (idx < 0) return;
      const add = Math.min(leftover, key === 'address' ? leftover * 0.45 : leftover * 0.2);
      widths[idx] += add;
      leftover -= add;
    });
    if (leftover > 0 && widths.length) {
      widths[widths.length - 1] += leftover;
    }
  }

  const columnStyles = Object.fromEntries(
    cols.map((c, i) => {
      const t = `${c.key || ''} ${c.label || ''}`.toLowerCase();
      const left = /\b(guest_name|name|address|email|purpose|source|booking_code|id_number)\b/.test(
        t
      );
      const wrap = WRAP_KEYS.has(c.key) || !FIXED_FIT_KEYS.has(c.key);
      return [
        i,
        {
          halign: left ? 'left' : 'center',
          valign: 'middle',
          cellWidth: widths[i],
          overflow: 'linebreak',
          cellPadding: wrap
            ? { top: 0.5, bottom: 0.5, left: 0.4, right: 0.4 }
            : { top: 0.5, bottom: 0.5, left: 0.35, right: 0.35 },
        },
      ];
    })
  );

  autoTable(doc, {
    startY: y,
    head,
    body,
    styles: {
      font: 'helvetica',
      fontStyle: 'normal',
      fontSize,
      cellPadding: { top: 0.5, bottom: 0.5, left: 0.4, right: 0.4 },
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'center',
      textColor: PDF_BRAND.text,
      lineColor: PDF_BRAND.softBorder,
      lineWidth: 0.12,
      minCellHeight: 4,
    },
    headStyles: {
      fillColor: PDF_BRAND.titleTeal,
      textColor: PDF_BRAND.white,
      fontStyle: 'bold',
      fontSize: 4.5,
      halign: 'center',
      valign: 'middle',
      cellPadding: { top: 0.8, bottom: 0.8, left: 0.35, right: 0.35 },
      overflow: 'linebreak',
      minCellHeight: 5.5,
    },
    bodyStyles: {
      fontStyle: 'normal',
      fontSize,
    },
    alternateRowStyles: {
      fillColor: PDF_BRAND.zebra,
    },
    columnStyles,
    margin: { left: marginX, right: marginX, top: 14, bottom: footerH + 2 },
    tableWidth: contentW,
    showHead: 'everyPage',
    didDrawPage: () => {
      const pageNumber = doc.internal.getNumberOfPages();
      if (pageNumber > 1) {
        doc.setFillColor(...PDF_BRAND.titleTeal);
        doc.rect(0, 0, pageW, 8, 'F');
        doc.setTextColor(...PDF_BRAND.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(doc.splitTextToSize(title, contentW)[0] || 'Police Report', pageW / 2, 5, {
          align: 'center',
        });
      }

      doc.setFillColor(...PDF_BRAND.cream);
      doc.rect(0, pageH - footerH, pageW, footerH, 'F');
      doc.setDrawColor(...PDF_BRAND.mint);
      doc.setLineWidth(0.4);
      doc.line(0, pageH - footerH, pageW, pageH - footerH);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5.5);
      doc.setTextColor(...PDF_BRAND.muted);
      doc.text(`Rows: ${dataRows.length}`, marginX, pageH - 3);
      doc.text(`Page ${pageNumber}`, pageW - marginX, pageH - 3, { align: 'right' });
      doc.text('Police Report · Guest register', pageW / 2, pageH - 3, {
        align: 'center',
      });
    },
  });

  const name = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  doc.save(name);
}
